import { useMutation, useQuery, useQueryClient, UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { enum_api_uri } from "../utils/enum";
import axiosInstance from "./axiosInstance";

// 모닝택배 기사 관리 (기사명 / 전화번호 / 차량번호)
export interface Driver {
    driver_no: number;
    name: string;
    phone: string;
    car_no: string;
}

export interface DriverListResponse {
    limit: number;
    current_page: number;
    last_page: number;
    total_count: number;
    data_list: Driver[];
}

interface DriverListRequest {
    search?: string;
    searchtxt?: string;
    page?: number;
    limit?: number | string;
}

interface DriverBody {
    name: string;
    phone: string;
    car_no: string;
}

export const useGetConsoleDrivers = (params: DriverListRequest): UseQueryResult<DriverListResponse, Error> => {
    return useQuery({
        queryKey: ["useGetConsoleDrivers", params.search, params.searchtxt, params.page, params.limit],
        queryFn: async () => {
            const response = await axiosInstance.get(enum_api_uri.get_console_drivers, { params });
            return response.data.data;
        },
    });
};

export const useGetConsoleDriverDetail = (driver_no: string): UseQueryResult<Driver, Error> => {
    const url = enum_api_uri.get_console_driver_detail.replace(":driver_no", driver_no);
    return useQuery({
        queryKey: ["useGetConsoleDriverDetail", driver_no],
        queryFn: async () => {
            const response = await axiosInstance.get(url);
            return response.data.data;
        },
        enabled: !!driver_no,
    });
};

export const usePostConsoleDriver = (): UseMutationResult<any, Error, DriverBody> => {
    return useMutation({
        mutationFn: async (body: DriverBody) => {
            const response = await axiosInstance.post(enum_api_uri.post_console_driver, body);
            return response.data;
        },
    });
};

export const usePutConsoleDriver = (): UseMutationResult<any, Error, DriverBody & { driver_no: string }> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ driver_no, ...body }: DriverBody & { driver_no: string }) => {
            const url = enum_api_uri.put_console_driver.replace(":driver_no", driver_no);
            const response = await axiosInstance.put(url, body);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["useGetConsoleDriverDetail"] });
        },
    });
};

export const useDeleteConsoleDriver = (
    onSuccessCallback?: () => void,
): UseMutationResult<any, Error, { driver_no: number[] }> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (body: { driver_no: number[] }) => {
            const response = await axiosInstance.delete(enum_api_uri.delete_console_driver, { data: body });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["useGetConsoleDrivers"] });
            onSuccessCallback?.();
        },
    });
};
